import { useParams, Link } from 'react-router-dom';
import SeoHelmet from '@/components/feature/SeoHelmet';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteInfo, articles, categories } from '@/mocks/data';

function formatViews(num: number): string {
  if (num >= 10000) {
    return `${(num / 10000).toFixed(1)}万`;
  }
  return num.toLocaleString();
}

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground-950 mb-4">文章未找到</h1>
          <Link to="/" className="text-primary-600 hover:underline">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const category = categories.find((c) => c.slug === article.categorySlug);
  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 3);

  const schemas = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.summary,
      image: article.image,
      datePublished: article.date,
      author: {
        '@type': 'Organization',
        name: article.author,
      },
      publisher: {
        '@type': 'Organization',
        name: siteInfo.name,
        logo: { '@type': 'ImageObject', url: siteInfo.logo },
      },
      articleSection: article.category,
      keywords: article.tags.join(','),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: `${article.title} - ${siteInfo.name}`,
      description: article.summary,
      url: `${siteInfo.url}/article/${article.slug}`,
      isPartOf: { '@type': 'WebSite', name: siteInfo.name, url: siteInfo.url },
      about: { '@type': 'Thing', name: article.category },
    },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <SeoHelmet
        title={article.title}
        description={article.summary}
        keywords={`${article.tags.join(',')},91吃瓜,91爆料`}
        ogImage={article.image}
        ogType="article"
        canonicalPath={`/article/${article.slug}`}
        article={{
          publishedTime: article.date,
          author: article.author,
          section: article.category,
          tags: article.tags,
        }}
        schemas={schemas}
      />
      <Navbar />

      <div className="pt-20 md:pt-24">
        <Breadcrumb
          items={[
            { label: category?.name || article.category, href: `/category/${article.categorySlug}` },
            { label: '正文' },
          ]}
        />
      </div>

      {/* Article Header */}
      <article className="px-4 md:px-8 lg:px-12 py-6 md:py-10 max-w-5xl mx-auto">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* Left: image */}
          <div className="lg:w-1/2">
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-[260px] md:h-[340px] object-cover"
                loading="eager"
              />
            </div>
          </div>

          {/* Right: meta */}
          <div className="lg:w-1/2 flex flex-col justify-center">
            {article.isExclusive && (
              <span className="inline-block self-start px-3 py-1 bg-primary-500 text-background-50 text-xs font-medium rounded-full mb-4">
                独家爆料
              </span>
            )}
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight">
              {article.title}
            </h1>
            <p className="text-foreground-500 text-sm md:text-base leading-relaxed mt-4">
              {article.summary}
            </p>

            <div className="flex flex-wrap items-center gap-3 mt-6 text-xs text-foreground-400">
              <span className="text-primary-600 font-medium">{article.category}</span>
              <span>·</span>
              <span>{article.author}</span>
              <span>·</span>
              <span>{article.date}</span>
              <span>·</span>
              <span>{formatViews(article.views)} 阅读</span>
            </div>

            <div className="flex flex-wrap gap-2 mt-4">
              {article.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/category/${article.categorySlug}`}
                  className="px-2.5 py-1 bg-background-100 text-foreground-600 text-xs rounded-full hover:bg-primary-100 hover:text-primary-700 transition-colors"
                >
                  {tag}
                </Link>
              ))}
            </div>

            <button className="mt-6 inline-flex items-center gap-2 self-start px-5 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm font-medium text-foreground-700 hover:bg-foreground-950 hover:text-background-50 hover:border-foreground-950 transition-all">
              分享黑料
              <i className="ri-share-forward-line w-4 h-4 flex items-center justify-center" />
            </button>
          </div>
        </div>

        {/* Article Content */}
        <div className="mt-10 md:mt-14">
          <div className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed">
            {article.content.split('\n\n').map((paragraph, idx) => (
              <p key={idx} className="mb-5 text-sm md:text-base leading-[1.8]">
                {paragraph}
              </p>
            ))}
          </div>
        </div>

        {/* Share bar */}
        <div className="mt-10 pt-6 border-t border-background-200/70 flex items-center justify-between">
          <span className="text-sm text-foreground-500">
            本文由 {article.author} 发布于 {article.date}
          </span>
          <div className="flex items-center gap-2">
            <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500 hover:bg-primary-500 hover:text-background-50 transition-colors">
              <i className="ri-weibo-line w-4 h-4 flex items-center justify-center" />
            </button>
            <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500 hover:bg-primary-500 hover:text-background-50 transition-colors">
              <i className="ri-wechat-line w-4 h-4 flex items-center justify-center" />
            </button>
            <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500 hover:bg-primary-500 hover:text-background-50 transition-colors">
              <i className="ri-link w-4 h-4 flex items-center justify-center" />
            </button>
          </div>
        </div>
      </article>

      {/* Comments Section */}
      <section className="bg-background-100 py-10 md:py-16">
        <div className="px-4 md:px-8 lg:px-12 max-w-5xl mx-auto">
          <div className="text-center mb-8 md:mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-primary-100 text-primary-700 text-xs font-medium mb-3">
              <span className="w-2 h-2 bg-primary-500 rotate-45 inline-block" />
              站友辣评
            </div>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-primary-700">
              站友辣评
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              {
                user: '前排吃瓜群众',
                level: '资深瓜农',
                content: '这个瓜太劲爆了！不过说实话，圈里人都知道这事儿迟早会爆出来，只是没想到这么快。',
              },
              {
                user: '理性看瓜',
                level: '金牌线人',
                content: '91吃瓜网的爆料一直比较靠谱，不像某些营销号只会跟风。这篇报道的信源标注很清楚。',
              },
              {
                user: '路过打酱油',
                level: '初级瓜农',
                content: '刚吃完上一个瓜，这个又来了。感觉今年娱乐圈的瓜田特别丰收啊，根本吃不完。',
              },
            ].map((comment, idx) => (
              <div
                key={idx}
                className="bg-background-50 rounded-xl p-5 border border-background-200/70"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center">
                    <i className="ri-user-line text-primary-600 w-4 h-4 flex items-center justify-center" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-950">{comment.user}</p>
                    <p className="text-xs text-foreground-400">{comment.level}</p>
                  </div>
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed">{comment.content}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="bg-background-50 py-10 md:py-16">
          <div className="px-4 md:px-8 lg:px-12 max-w-5xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6 md:mb-8">
              还想再吃
              <span className="text-primary-500 ml-2">相关黑料</span>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.id}
                  to={`/article/${ra.slug}`}
                  className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="relative h-[160px] md:h-[180px] overflow-hidden bg-background-100">
                    <img
                      src={ra.image}
                      alt={ra.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-2 text-xs text-foreground-400 mb-2">
                      <span>{ra.date}</span>
                      <span>·</span>
                      <span>{formatViews(ra.views)} 阅读</span>
                    </div>
                    <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {ra.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}